import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, EnforcementAction, Violation } from "../../types";
import { getPendingEnforcement, getAllEnforcement, approveEnforcement,
         getViolations, getViolationYears, getCompanies, getSamplingSchedule, getAnnualReport,
         exportAnnualReportIcisXml, exportAnnualReportIcisSubmission,
         upsertSncPublication, updateLocalLimits,
         upsertContamination, logout } from "../../api/client";
import NotificationBell from "../NotificationBell";
import InspectionsPanel from "../Inspections/InspectionsPanel";
import CASamplingPanel from "../Inspections/CASamplingPanel";
import CommunicationsPanel from "../Communications/CommunicationsPanel";
import PpsReport from "../PpsReport";
import NpdesAlertBar from "../NpdesAlertBar";
import { VIOLATION_PLAIN, ENFORCEMENT_PLAIN } from "../../ui/labels";

const STATUS_PLAIN: Record<string, string> = { pending: "Awaiting approval", approved: "Approved", sent: "Sent" };

interface Props { user: User; onLogout: () => void; }

export default function CoordinatorDashboard({ user, onLogout }: Props) {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [tab, setTab]               = useState<"pending"|"violations"|"schedule"|"inspections"|"casampling"|"communications"|"annual"|"pps">("pending");
  const [actions, setActions]       = useState<EnforcementAction[]>([]);
  // Pending only (the default), or every action including those already approved.
  const [showAll, setShowAll]       = useState(false);
  const [allActions, setAllActions] = useState<EnforcementAction[]>([]);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [companies, setCompanies]   = useState<any[]>([]);
  const [companyFilter, setCompanyFilter] = useState<string>("");
  // Violations tab shows one year at a time; the list grows every year.
  const [violationYear, setViolationYear]   = useState<number>(currentYear);
  const [violationYears, setViolationYears] = useState<number[]>([]);
  const [scheduleRows, setScheduleRows]   = useState<any[]>([]);
  const [scheduleCompany, setScheduleCompany] = useState<string>("");
  const [selected, setSelected]     = useState<EnforcementAction | null>(null);
  const [notes, setNotes]           = useState("");
  // The signature defaults to the coordinator's own full name; it can be changed.
  const [signature, setSignature]   = useState(user.full_name ?? "");
  const [processing, setProcessing] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [fetchError, setFetchError] = useState<string>("");
  const [loading, setLoading]       = useState(true);
  // Annual report (40 CFR 403.12(i)) — lazy-loaded when the tab is opened.
  const [reportYear, setReportYear]       = useState<number>(currentYear);
  const [annual, setAnnual]               = useState<any | null>(null);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [annualError, setAnnualError]     = useState<string>("");
  // SNC public-notice (40 CFR 403.8(f)(2)(viii)) recording, within the Annual tab.
  const [sncPubDate, setSncPubDate]   = useState<string>("");
  const [sncPubName, setSncPubName]   = useState<string>("");
  const [sncPubSaving, setSncPubSaving] = useState(false);
  // Local-limits metadata (ICIS ControlAuthorityProgramInformation).
  const [llAdoption, setLlAdoption] = useState<string>("");
  const [llEvaluation, setLlEvaluation] = useState<string>("");
  const [llCodes, setLlCodes]       = useState<string>("");
  const [llSaving, setLlSaving]     = useState(false);
  // POTW discharge/biosolids contamination indicators (ICIS CAP block).
  const [discharge, setDischarge]   = useState(false);
  const [dischargeText, setDischargeText] = useState<string>("");
  const [biosolids, setBiosolids]   = useState(false);
  const [biosolidsText, setBiosolidsText] = useState<string>("");
  const [contamSaving, setContamSaving] = useState(false);

  const loadAnnual = (year: number) => {
    setAnnualLoading(true);
    setAnnualError("");
    getAnnualReport(year)
      .then(r => {
        setAnnual(r.data);
        const pub = r.data?.snc_publication;
        setSncPubDate(pub?.published_date ?? "");
        setSncPubName(pub?.publication_name ?? "");
        const ll = r.data?.local_limits;
        setLlAdoption(ll?.adoption_date ?? "");
        setLlEvaluation(ll?.evaluation_date ?? "");
        setLlCodes((ll?.parameter_codes ?? []).join(", "));
        const cm = r.data?.contamination;
        setDischarge(!!cm?.discharge);
        setDischargeText(cm?.discharge_text ?? "");
        setBiosolids(!!cm?.biosolids);
        setBiosolidsText(cm?.biosolids_text ?? "");
      })
      .catch(e => setAnnualError(e?.response?.data?.error ?? e?.message ?? "Failed to load report"))
      .finally(() => setAnnualLoading(false));
  };

  const saveSncPublication = () => {
    if (!annual || !sncPubDate) return;
    setSncPubSaving(true);
    setAnnualError("");
    upsertSncPublication({
      year: annual.reporting_year, published_date: sncPubDate,
      publication_name: sncPubName || undefined,
    })
      .then(() => loadAnnual(annual.reporting_year))   // refresh so IU rows reflect published=Y
      .catch(e => setAnnualError(e?.response?.data?.error ?? e?.message ?? "Couldn't save the SNC notice"))
      .finally(() => setSncPubSaving(false));
  };

  const saveLocalLimits = () => {
    if (!annual) return;
    setLlSaving(true);
    setAnnualError("");
    updateLocalLimits({
      adoption_date: llAdoption || null,
      evaluation_date: llEvaluation || null,
      parameter_codes: llCodes.split(",").map(s => s.trim()).filter(Boolean),
    })
      .then(() => loadAnnual(annual.reporting_year))
      .catch(e => setAnnualError(e?.response?.data?.error ?? e?.message ?? "Couldn't save local limits"))
      .finally(() => setLlSaving(false));
  };

  const saveContamination = () => {
    if (!annual) return;
    setContamSaving(true);
    setAnnualError("");
    upsertContamination({
      year: annual.reporting_year,
      discharge, discharge_text: dischargeText || undefined,
      biosolids, biosolids_text: biosolidsText || undefined,
    })
      .then(() => loadAnnual(annual.reporting_year))
      .catch(e => setAnnualError(e?.response?.data?.error ?? e?.message ?? "Couldn't save contamination"))
      .finally(() => setContamSaving(false));
  };

  const openAnnualTab = () => {
    setTab("annual");
    if (!annual || annual.reporting_year !== reportYear) loadAnnual(reportYear);
  };

  const downloadBlob = (data: BlobPart, filename: string, type: string) => {
    const url = URL.createObjectURL(new Blob([data], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadAnnualJson = () => {
    if (!annual) return;
    downloadBlob(JSON.stringify(annual, null, 2),
      `pretreatment_annual_report_${annual.reporting_year}.json`, "application/json");
  };

  const downloadAnnualIcisXml = () => {
    if (!annual) return;
    setAnnualError("");
    exportAnnualReportIcisXml(annual.reporting_year)
      .then(r => downloadBlob(r.data,
        `pretreatment_annual_${annual.reporting_year}_icis_v5.14.xml`, "application/xml"))
      .catch(e => setAnnualError(e?.response?.data?.error ?? e?.message ?? "ICIS XML export failed"));
  };

  const downloadAnnualIcisSubmission = () => {
    if (!annual) return;
    setAnnualError("");
    exportAnnualReportIcisSubmission(annual.reporting_year)
      .then(r => downloadBlob(r.data,
        `pretreatment_annual_${annual.reporting_year}_icis_submission_v5.14.xml`, "application/xml"))
      .catch(e => setAnnualError(e?.response?.data?.error ?? e?.message ?? "ICIS submission export failed"));
  };

  const reload = () => {
    const err = (label: string) => (e: any) =>
      setFetchError(`${label}: ${e?.response?.data?.error ?? e?.message ?? "failed"}`);
    setFetchError("");
    setLoading(true);
    Promise.all([
      getPendingEnforcement().then(r => setActions(r.data)).catch(err("Enforcement")),
      getViolations(undefined, violationYear).then(r => setViolations(r.data)).catch(err("Violations")),
      getViolationYears().then(r => setViolationYears(r.data)).catch(() => {}),
      getCompanies().then(r => setCompanies(r.data)).catch(err("Companies")),
      getSamplingSchedule().then(r => setScheduleRows(r.data)).catch(err("Schedule")),
    ]).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const chooseViolationYear = (y: number) => {
    setViolationYear(y);
    getViolations(undefined, y).then(r => setViolations(r.data)).catch(() => {});
  };
  // The current year is always offered, even before it has any violations.
  const violationYearOptions = Array.from(new Set([currentYear, ...violationYears])).sort((a, b) => b - a);

  const loadAll = () => getAllEnforcement().then(r => setAllActions(r.data)).catch(() => {});
  const chooseView = (all: boolean) => {
    setShowAll(all); selectAction(null);
    if (all) loadAll();
  };

  const selectAction = (a: EnforcementAction | null) => {
    setSelected(a); setNotes(""); setSignature(user.full_name ?? ""); setApproveError("");
  };

  const handleApprove = async () => {
    if (!selected || !signature.trim()) return;
    setProcessing(true);
    setApproveError("");
    try {
      await approveEnforcement(selected.id, { notes, e_signature: signature.trim() });
      selectAction(null); reload();
      if (showAll) loadAll();
    } catch (e: any) {
      setApproveError(e?.response?.data?.error ?? e?.message ?? "The approval was not saved. Try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => { logout().finally(() => onLogout()); };

  const companyName = (id: number) =>
    companies.find(c => c.id === id)?.name ?? `Company #${id}`;

  const filteredViolations = companyFilter
    ? violations.filter((v: any) => String(v.company_id) === companyFilter)
    : violations;

  return (
    <div style={s.page}>
      <div style={s.stickyTop}>
        <header style={s.header}>
          <a href="/" style={{...s.brand, textDecoration:"none", color:"inherit"}}
             title="Control Authority home">Regreports PIMS</a>
          <span style={s.role}>Coordinator</span>
          <NotificationBell onGoToSchedule={() => {
            setTab("schedule");
            getSamplingSchedule().then(r => setScheduleRows(r.data)).catch(() => {});
          }} />
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </header>

        <div style={s.tabs}>
          <button style={{...s.tab, ...(tab==="pending" ? s.activeTab : {})}}
            onClick={() => setTab("pending")}>
            Pending Approvals {actions.length > 0 && `(${actions.length})`}
          </button>
          <button style={{...s.tab, ...(tab==="violations" ? s.activeTab : {})}}
            onClick={() => setTab("violations")}>
            Violations {violations.length > 0 && `(${violations.length})`}
          </button>
          <button style={{...s.tab, ...(tab==="schedule" ? s.activeTab : {}),
            ...(scheduleRows.filter(r=>r.status==="overdue").length > 0 ? {color:"#c53030"} : {})}}
            onClick={() => setTab("schedule")}>
            Schedule {scheduleRows.filter(r=>r.status==="overdue").length > 0
              && `(${scheduleRows.filter(r=>r.status==="overdue").length} overdue)`}
          </button>
          <button style={{...s.tab, ...(tab==="inspections" ? s.activeTab : {})}}
            onClick={() => setTab("inspections")}>
            Inspections
          </button>
          <button style={{...s.tab, ...(tab==="casampling" ? s.activeTab : {})}}
            onClick={() => setTab("casampling")}>
            CA Sampling
          </button>
          <button style={{...s.tab, ...(tab==="communications" ? s.activeTab : {})}}
            onClick={() => setTab("communications")}>
            Communications
          </button>
          <button style={{...s.tab, ...(tab==="annual" ? s.activeTab : {})}}
            onClick={openAnnualTab}>
            Annual Report
          </button>
          <button style={{...s.tab, ...(tab==="pps" ? s.activeTab : {})}}
            onClick={() => setTab("pps")}>
            SCDES Report
          </button>
        </div>
      </div>

      <NpdesAlertBar />

      <div style={s.content}>

        {loading && (
          <div style={{ textAlign:"center", padding:48, color:"#718096" }}>
            <div style={{ fontSize:28, marginBottom:8 }}>⏳</div>
            Loading data…
          </div>
        )}

        {fetchError && (
          <div style={{ background:"#fff5f5", border:"1px solid #fc8181", borderRadius:6,
                         color:"#c53030", padding:"10px 14px", margin:"0 0 14px", fontSize:13 }}>
            <strong>Data load error —</strong> {fetchError}
            <br /><span style={{opacity:0.7, fontSize:11}}>
              Check that the backend is running and you are logged in.
            </span>
          </div>
        )}

        {!loading && tab === "pending" && (
          <div style={s.splitPane}>
            <div style={s.list}>
              <div style={s.viewToggle} role="group" aria-label="Which enforcement actions">
                <button type="button" aria-pressed={!showAll}
                  style={{...s.viewBtn, ...(!showAll ? s.viewBtnOn : {})}}
                  onClick={() => chooseView(false)}>Pending ({actions.length})</button>
                <button type="button" aria-pressed={showAll}
                  style={{...s.viewBtn, ...(showAll ? s.viewBtnOn : {})}}
                  onClick={() => chooseView(true)}>Show all</button>
              </div>
              {(showAll ? allActions : actions).length === 0
                ? <p style={s.empty}>{showAll ? "No enforcement actions yet." : "No pending enforcement actions."}</p>
                : (showAll ? allActions : actions).map(a => (
                  <div key={a.id} style={{...s.card, ...(selected?.id===a.id ? s.selectedCard : {})}}
                       onClick={() => selectAction(a)}>
                    <div style={s.cardTop}>
                      <strong>{ENFORCEMENT_PLAIN[a.response_level] ?? a.response_level.replace(/_/g," ")}</strong>
                      {a.fine_amount > 0 && <span style={s.fine}>${a.fine_amount.toFixed(2)}</span>}
                    </div>
                    <div style={s.cardSub}><strong style={{color:"#2d3748"}}>{companyName(a.company_id)}</strong></div>
                    <div style={s.cardSub}>
                      {[a.parameter_name, a.sample_evidence?.sample_date ?? a.violation_date].filter(Boolean).join(" · ")
                        || `Violation #${a.violation_id}`}
                    </div>
                    {showAll && (
                      <div style={{...s.statusChip, ...(a.status === "pending" ? s.statusPending
                                   : a.status === "withdrawn" ? s.statusWithdrawn : s.statusDone)}}>
                        {a.status === "pending" ? "Pending"
                          : `${a.status.charAt(0).toUpperCase()}${a.status.slice(1)}`
                            + (a.approval_date ? ` ${a.approval_date.slice(0, 10)}` : "")}
                      </div>
                    )}
                  </div>
                ))
              }
            </div>

            {selected && (
              <div style={s.detail}>
                <h3 style={s.detailTitle}>
                  {selected.status === "pending" ? "Review " : ""}Enforcement Action #{selected.id}
                </h3>

                {/* The sample that triggered this violation — evidence for the AO. */}
                <div style={s.evidenceBox}>
                  <div style={s.evidenceHead}>
                    Triggering sample
                    {selected.parameter_name && <span style={s.evidenceParam}>{selected.parameter_name}</span>}
                  </div>
                  {selected.sample_evidence ? (
                    <>
                      <div style={s.evidenceRow}>
                        <span style={s.evidenceK}>Sampled</span>
                        <span>{selected.sample_evidence.sample_date}
                          {selected.sample_evidence.sampler_name ? ` · ${selected.sample_evidence.sampler_name}` : ""}</span>
                      </div>
                      <div style={s.evidenceRow}>
                        <span style={s.evidenceK}>Measured</span>
                        <span>
                          {selected.sample_evidence.non_detect
                            ? `Non-detect (< ${selected.sample_evidence.reported_limit_mg_l ?? "?"} mg/L)`
                            : selected.sample_evidence.measured_value != null
                              ? `${selected.sample_evidence.measured_value} ${selected.sample_evidence.measured_unit ?? ""}`.trim()
                              : "—"}
                        </span>
                      </div>
                      {selected.sample_evidence.limit_value != null && (
                        <div style={s.evidenceRow}>
                          <span style={s.evidenceK}>Limit</span>
                          <span>{selected.sample_evidence.limit_value} ({selected.sample_evidence.limit_basis})</span>
                        </div>
                      )}
                      {selected.sample_evidence.exceedance_percent != null && (
                        <div style={s.evidenceRow}>
                          <span style={s.evidenceK}>Exceedance</span>
                          <span style={s.evidenceExceed}>+{selected.sample_evidence.exceedance_percent.toFixed(1)}%</span>
                        </div>
                      )}
                      <div style={s.evidenceRow}>
                        <span style={s.evidenceK}>Sample #</span>
                        <span>{selected.sample_evidence.sample_id}
                          {selected.sample_evidence.flow_mgd != null ? ` · ${selected.sample_evidence.flow_mgd} MGD` : ""}</span>
                      </div>
                      <button type="button" style={s.evidenceLink}
                        onClick={() => navigate(`/samples/${selected.sample_evidence!.sample_id}`)}>
                        View full sample →
                      </button>
                    </>
                  ) : (
                    <div style={s.evidenceNone}>
                      {selected.violation_type === "missing_sample"
                        ? "No sample — this violation is for a missing required sample."
                        : "Not tied to a single sample (e.g. period-average or flow violation)."}
                    </div>
                  )}
                </div>

                <div style={s.letterBox}>{selected.auto_generated_response}</div>
                {selected.status !== "pending" ? (
                  <>
                    <div style={s.doneBox}>
                      <strong>{selected.status.charAt(0).toUpperCase() + selected.status.slice(1)}</strong>
                      {selected.approval_date && ` ${selected.approval_date.slice(0, 10)}`}
                      {selected.e_signature && <> · signed by {selected.e_signature}</>}
                      {selected.coordinator_notes && <div style={{ marginTop:6 }}>Notes: {selected.coordinator_notes}</div>}
                    </div>
                    <div style={s.actionRow}>
                      <button style={s.cancelBtn} onClick={() => selectAction(null)}>Close</button>
                    </div>
                  </>
                ) : (<>
                <label style={s.label}>Coordinator Notes</label>
                <textarea style={s.textarea} value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add notes or modifications…" />
                <label style={s.label}>Electronic Signature (type full name)</label>
                <input style={s.input} value={signature}
                  onChange={e => setSignature(e.target.value)}
                  placeholder="Full name as electronic signature" />
                <div style={s.actionRow}>
                  <button style={s.approveBtn} onClick={handleApprove}
                    disabled={!signature.trim() || processing}>
                    {processing ? "Approving…" : "Approve & Send"}
                  </button>
                  <button style={s.cancelBtn} onClick={() => selectAction(null)}>Cancel</button>
                </div>
                {approveError && (
                  <div role="alert" style={s.approveError}>
                    <strong>Not approved —</strong> {approveError}
                  </div>
                )}
                </>)}
              </div>
            )}
          </div>
        )}

        {!loading && tab === "schedule" && (
          <div>
            <div style={s.filterBar}>
              <label style={s.filterLabel}>Filter by Company</label>
              <select style={s.filterSelect} value={scheduleCompany}
                onChange={e => {
                  setScheduleCompany(e.target.value);
                  getSamplingSchedule(e.target.value ? parseInt(e.target.value) : undefined)
                    .then(r => setScheduleRows(r.data)).catch(() => {});
                }}>
                <option value="">All companies</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {scheduleCompany && (
                <button style={s.clearFilter} onClick={() => {
                  setScheduleCompany("");
                  getSamplingSchedule().then(r => setScheduleRows(r.data)).catch(() => {});
                }}>Clear filter</button>
              )}
              <div style={{display:"flex", gap:8, marginLeft:"auto", flexWrap:"wrap" as const}}>
                {scheduleRows.filter(r=>r.status==="overdue").length  > 0 && <span style={s.chipOverdue}>{scheduleRows.filter(r=>r.status==="overdue").length} Overdue</span>}
                {scheduleRows.filter(r=>r.status==="due_soon").length > 0 && <span style={s.chipDueSoon}>{scheduleRows.filter(r=>r.status==="due_soon").length} Due Soon</span>}
                {scheduleRows.filter(r=>r.status==="never").length    > 0 && <span style={s.chipNever}>{scheduleRows.filter(r=>r.status==="never").length} Never Sampled</span>}
                {scheduleRows.filter(r=>r.status==="current").length  > 0 && <span style={s.chipCurrent}>{scheduleRows.filter(r=>r.status==="current").length} Current</span>}
              </div>
            </div>

            {scheduleRows.length === 0
              ? <p style={{color:"#718096", padding:16}}>No frequency requirements found.</p>
              : <div style={s.tableWrap}><table style={s.table}>
                  <thead><tr>
                    <th style={s.th}>Company</th>
                    <th style={s.th}>Permit</th>
                    <th style={s.th}>Parameter</th>
                    <th style={s.th}>Frequency</th>
                    <th style={s.th}>Sample Type</th>
                    <th style={s.th}>Last Sampled</th>
                    <th style={s.th} title="Sampling days so far this month, against how many the frequency calls for">This Month</th>
                    <th style={s.th} title="Results for the next sampling period must be submitted by this date">Submit By</th>
                    <th style={s.th}>Status</th>
                  </tr></thead>
                  <tbody>{scheduleRows.map((r: any, i: number) => (
                    <tr key={i} style={{
                      background: r.status==="overdue" ? "#fff5f5" : r.status==="due_soon" ? "#fffff0" : undefined
                    }}>
                      <td style={s.td}><strong>{r.company_name}</strong></td>
                      <td style={s.td}>{r.permit_number}</td>
                      <td style={s.td}>{r.parameter_name}{r.is_monitor_report && <span title="Monitor and report: sampled and reported, no numeric limit" style={{marginLeft:6, fontSize:10, fontWeight:700, color:"#975a16", background:"#fefcbf", border:"1px solid #f6e05e", borderRadius:8, padding:"0 5px"}}>MR</span>}</td>
                      <td style={s.td}>{r.frequency_description}</td>
                      <td style={{...s.td, textTransform:"capitalize"}}>{r.sample_type ?? "—"}</td>
                      <td style={s.td}>{r.last_sample_date ?? <em style={{color:"#a0aec0"}}>Never</em>}</td>
                      <td style={s.td}>{r.month_required != null
                        ? <span style={{color: r.month_sample_count >= r.month_required ? "#276749" : "#4a5568"}}>
                            {r.month_sample_count} of {r.month_required}</span>
                        : <span style={{color:"#a0aec0"}}>—</span>}</td>
                      <td style={s.td}>{r.next_due_date ?? "—"}</td>
                      <td style={s.td}>
                        {r.status==="overdue"  && <span style={s.schedOverdue}>Overdue {r.days_overdue}d</span>}
                        {r.status==="due_soon" && <span style={s.schedDueSoon}>Due Soon</span>}
                        {r.status==="never"    && <span style={s.schedNever}>Never Sampled</span>}
                        {r.status==="current"  && <span style={s.schedCurrent}>Current</span>}
                      </td>
                    </tr>
                  ))}</tbody>
                </table></div>
            }
          </div>
        )}

        {!loading && tab === "violations" && (
          <div>
            {/* Filter bar */}
            <div style={s.filterBar}>
              <label style={s.filterLabel}>Year</label>
              <select style={{...s.filterSelect, minWidth:90}} value={violationYear} aria-label="Year"
                onChange={e => chooseViolationYear(parseInt(e.target.value))}>
                {violationYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <label style={s.filterLabel}>Company</label>
              <select style={s.filterSelect} value={companyFilter}
                onChange={e => setCompanyFilter(e.target.value)}>
                <option value="">All companies</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {companyFilter && (
                <button style={s.clearFilter} onClick={() => setCompanyFilter("")}>
                  Clear filter
                </button>
              )}
              <span style={s.filterCount}>
                {filteredViolations.length} violation{filteredViolations.length !== 1 ? "s" : ""} in {violationYear}
              </span>
            </div>

            {filteredViolations.length === 0 ? (
              <p style={s.allGood}>✓ No violations in {violationYear}{companyFilter ? " for this company" : ""}</p>
            ) : (
              <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Company</th>
                    <th style={s.th}>Parameter</th>
                    <th style={s.th}>Violation</th>
                    <th style={s.th}>Result</th>
                    <th style={s.th}>Limit</th>
                    <th style={s.th}>Over</th>
                    <th style={s.th}>Severity</th>
                    <th style={s.th}>Enforcement</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredViolations.map((v: Violation) => (
                    <tr key={v.id}
                      onClick={v.sample_id ? () => navigate(`/samples/${v.sample_id}`) : undefined}
                      title={v.sample_id ? "Open the sample" : undefined}
                      style={{background: v.violation_severity === "major" ? "#fff5f5" : undefined,
                              cursor: v.sample_id ? "pointer" : undefined}}>
                      <td style={{...s.td, whiteSpace:"nowrap"}}>{v.violation_date}</td>
                      <td style={s.td}><strong>{companyName(v.company_id)}</strong></td>
                      <td style={s.td}>{v.parameter_name}</td>
                      <td style={s.td}>{VIOLATION_PLAIN[v.violation_type] ?? v.violation_type.replace(/_/g, " ")}</td>
                      <td style={s.td}>{v.evidence?.measured_value != null
                        ? (v.evidence.measured_is_average
                            ? `avg ${Number(v.evidence.measured_value.toPrecision(4))} ${v.evidence.measured_unit}`
                            : `${v.evidence.non_detect ? "<" : ""}${v.evidence.measured_value} ${v.evidence.measured_unit}`)
                        : "—"}</td>
                      <td style={s.td} title={v.evidence?.limit_basis ?? undefined}>{v.evidence?.limit_value != null
                        ? `${v.evidence.limit_value} ${v.evidence.measured_unit}` : "—"}</td>
                      <td style={s.td}>{v.exceedance_percent != null ? `${v.exceedance_percent.toFixed(1)}%` : "—"}</td>
                      <td style={s.td}>
                        <span style={
                          v.violation_severity === "major"    ? s.sevMajor :
                          v.violation_severity === "significant" ? s.sevSignificant :
                          s.sevMinor
                        }>
                          {v.violation_severity}
                        </span>
                      </td>
                      <td style={s.td}>
                        {(v.enforcement ?? []).length === 0
                          ? <span style={{color:"#a0aec0"}}>None</span>
                          : (v.enforcement ?? []).map((e, i) => (
                              <div key={i} style={{whiteSpace:"nowrap"}}>
                                {ENFORCEMENT_PLAIN[e.response_level] ?? e.response_level}
                                <span style={e.status === "pending" ? s.enfPending : s.enfIssued}>
                                  {" · "}{STATUS_PLAIN[e.status] ?? e.status}
                                  {e.status === "sent" && e.sent_at ? ` ${e.sent_at}`
                                    : e.status === "approved" && e.approval_date ? ` ${e.approval_date}` : ""}
                                </span>
                              </div>
                            ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {!loading && tab === "inspections" && (
          <InspectionsPanel />
        )}

        {!loading && tab === "casampling" && (
          <CASamplingPanel />
        )}

        {!loading && tab === "communications" && (
          <CommunicationsPanel />
        )}

        {!loading && tab === "pps" && <PpsReport />}

        {!loading && tab === "annual" && (
          <div>
            <div style={s.filterBar}>
              <label style={s.filterLabel}>Reporting Year</label>
              <select style={s.filterSelect} value={reportYear}
                onChange={e => { const y = parseInt(e.target.value); setReportYear(y); loadAnnual(y); }}>
                {Array.from({ length: 6 }, (_, i) => currentYear - i).map(y =>
                  <option key={y} value={y}>{y}</option>)}
              </select>
              <button style={s.clearFilter} onClick={() => loadAnnual(reportYear)}
                disabled={annualLoading}>
                {annualLoading ? "Loading…" : "Refresh"}
              </button>
              <div style={{display:"flex", gap:8, marginLeft:"auto", flexWrap:"wrap"}}>
                <button style={s.exportBtn} onClick={downloadAnnualIcisXml}
                  disabled={!annual || annualLoading}
                  title="ICIS-NPDES v5.14 PretreatmentProgramReport payload (pilot)">Download ICIS XML</button>
                <button style={s.exportBtn} onClick={downloadAnnualIcisSubmission}
                  disabled={!annual || annualLoading}
                  title="Full Exchange Network Document envelope — submittable form (pilot)">Download ICIS submission</button>
                <button style={s.exportBtn} onClick={downloadAnnualJson}
                  disabled={!annual || annualLoading}>Download JSON</button>
                <button style={s.exportBtn} onClick={() => window.print()}
                  disabled={!annual || annualLoading}>Print / Save PDF</button>
              </div>
            </div>

            {annualError && (
              <div style={{ background:"#fff5f5", border:"1px solid #fc8181", borderRadius:6,
                            color:"#c53030", padding:"10px 14px", marginBottom:14, fontSize:13 }}>
                <strong>Couldn't load the annual report —</strong> {annualError}
              </div>
            )}

            {annualLoading && !annual && (
              <p style={{ color:"#718096", padding:16 }}>⏳ Assembling report…</p>
            )}

            {annual && (
              <div>
                {/* POTW identity + period */}
                <div style={s.reportHead}>
                  <div>
                    <div style={s.reportTitle}>
                      Pretreatment Annual Report — {annual.reporting_year}
                    </div>
                    <div style={s.reportSub}>
                      {annual.potw?.name || <em style={{color:"#a0aec0"}}>POTW name not set</em>}
                      {annual.potw?.address ? ` · ${annual.potw.address}` : ""}
                    </div>
                    <div style={s.reportMeta}>
                      40 CFR 403.12(i) · Period {annual.period_start} → {annual.period_end}
                      {annual.generated_at && ` · Generated ${annual.generated_at.slice(0,10)}`}
                    </div>
                  </div>
                </div>

                {/* Summary cards */}
                <div style={s.cardGrid}>
                  {[
                    { label: "Industrial Users",   value: annual.summary?.industrial_users },
                    { label: "Categorical Users",  value: annual.summary?.categorical_users },
                    { label: "In SNC",             value: annual.summary?.in_snc, danger: (annual.summary?.in_snc ?? 0) > 0 },
                    { label: "With Violations",    value: annual.summary?.with_violations, danger: (annual.summary?.with_violations ?? 0) > 0 },
                    { label: "Total Samples",      value: annual.summary?.total_samples },
                    { label: "Total Violations",   value: annual.summary?.total_violations },
                    { label: "Enforcement Actions",value: annual.summary?.total_enforcement_actions },
                  ].map(c => (
                    <div key={c.label} style={s.statCard}>
                      <div style={{...s.statValue, ...(c.danger ? {color:"#c53030"} : {})}}>{c.value ?? 0}</div>
                      <div style={s.statLabel}>{c.label}</div>
                    </div>
                  ))}
                </div>

                {/* SNC facilities */}
                {annual.snc_facilities?.length > 0 && (
                  <div style={s.sncBox}>
                    <div style={s.sncTitle}>
                      Facilities in Significant Non-Compliance ({annual.snc_facilities.length})
                    </div>
                    <ul style={{margin:"6px 0 0", paddingLeft:20}}>
                      {annual.snc_facilities.map((f: any) => (
                        <li key={f.company_id} style={{fontSize:13, marginBottom:4}}>
                          <strong>{f.name}</strong>
                          {f.iu_permit_number ? ` (${f.iu_permit_number})` : ""}
                          {" — "}{f.parameters?.length ? f.parameters.join(", ") : "—"}
                          <span style={{color:"#718096"}}>
                            {" · "}half {f.halves?.join(" & ") || "—"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Local-limits program metadata (ICIS ControlAuthorityProgramInformation). */}
                <div style={s.sncPubBox}>
                  <div style={{fontSize:13, fontWeight:700, color:"#4a5568"}}>
                    Local limits
                    {annual.local_limits?.adoption_date || annual.local_limits?.parameter_codes?.length
                      ? <span style={s.sncPubOk}> ✓ recorded</span>
                      : <span style={s.sncPubWarn}> — not yet recorded</span>}
                  </div>
                  <p style={{fontSize:12, color:"#718096", margin:"4px 0 8px"}}>
                    Adoption / last-evaluation dates and the ICIS pollutant codes with local limits
                    (comma-separated, ≤5 chars each).
                  </p>
                  <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"center"}}>
                    <label style={{fontSize:12, color:"#4a5568"}}>
                      Adopted{" "}
                      <input type="date" value={llAdoption}
                        onChange={e => setLlAdoption(e.target.value)} style={s.sncPubInput} />
                    </label>
                    <label style={{fontSize:12, color:"#4a5568"}}>
                      Evaluated{" "}
                      <input type="date" value={llEvaluation}
                        onChange={e => setLlEvaluation(e.target.value)} style={s.sncPubInput} />
                    </label>
                    <input type="text" placeholder="Pollutant codes (e.g. 00310, 00530)"
                      value={llCodes} onChange={e => setLlCodes(e.target.value)}
                      style={{...s.sncPubInput, minWidth:220}} />
                    <button style={s.exportBtn} onClick={saveLocalLimits} disabled={llSaving}>
                      {llSaving ? "Saving…" : "Save local limits"}
                    </button>
                  </div>
                </div>

                {/* POTW contamination indicators (ICIS POTWDischarge/BiosolidsContamination). */}
                <div style={s.sncPubBox}>
                  <div style={{fontSize:13, fontWeight:700, color:"#4a5568"}}>
                    POTW contamination this period
                    {annual.contamination
                      ? <span style={s.sncPubOk}> ✓ recorded</span>
                      : <span style={s.sncPubWarn}> — not yet recorded</span>}
                  </div>
                  <p style={{fontSize:12, color:"#718096", margin:"4px 0 8px"}}>
                    Did the POTW experience IU-attributable interference/pass-through (discharge)
                    or biosolids contamination during the reporting period?
                  </p>
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
                      <label style={{fontSize:12, color:"#4a5568", fontWeight:600}}>
                        <input type="checkbox" checked={discharge}
                          onChange={e => setDischarge(e.target.checked)} /> Discharge (interference / pass-through)
                      </label>
                      <input type="text" placeholder="Explanation (optional)" value={dischargeText}
                        onChange={e => setDischargeText(e.target.value)}
                        style={{...s.sncPubInput, flex:1, minWidth:200}} />
                    </div>
                    <div style={{display:"flex", gap:8, alignItems:"center", flexWrap:"wrap"}}>
                      <label style={{fontSize:12, color:"#4a5568", fontWeight:600}}>
                        <input type="checkbox" checked={biosolids}
                          onChange={e => setBiosolids(e.target.checked)} /> Biosolids contamination
                      </label>
                      <input type="text" placeholder="Explanation (optional)" value={biosolidsText}
                        onChange={e => setBiosolidsText(e.target.value)}
                        style={{...s.sncPubInput, flex:1, minWidth:200}} />
                    </div>
                    <div>
                      <button style={s.exportBtn} onClick={saveContamination} disabled={contamSaving}>
                        {contamSaving ? "Saving…" : "Save contamination"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* SNC public notice (40 CFR 403.8(f)(2)(viii)) — required when any IU
                    is in SNC; supplies the ICIS SNCListingMonthYear + SNCPublishedIndicator. */}
                {annual.snc_facilities?.length > 0 && (
                  <div style={s.sncPubBox}>
                    <div style={{fontSize:13, fontWeight:700, color:"#4a5568"}}>
                      SNC public notice
                      {annual.snc_publication
                        ? <span style={s.sncPubOk}> ✓ published {annual.snc_publication.listing_month}</span>
                        : <span style={s.sncPubWarn}> — not yet recorded</span>}
                    </div>
                    <p style={{fontSize:12, color:"#718096", margin:"4px 0 8px"}}>
                      Record when the annual newspaper notice of SNC facilities ran. Until set,
                      each SNC IU exports with <code>SNCPublishedIndicator = N</code>.
                    </p>
                    <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"center"}}>
                      <label style={{fontSize:12, color:"#4a5568"}}>
                        Published date{" "}
                        <input type="date" value={sncPubDate}
                          onChange={e => setSncPubDate(e.target.value)} style={s.sncPubInput} />
                      </label>
                      <input type="text" placeholder="Newspaper (optional)" value={sncPubName}
                        onChange={e => setSncPubName(e.target.value)} style={s.sncPubInput} />
                      <button style={s.exportBtn} onClick={saveSncPublication}
                        disabled={!sncPubDate || sncPubSaving}>
                        {sncPubSaving ? "Saving…" : "Save notice"}
                      </button>
                    </div>
                  </div>
                )}

                {/* IU inventory */}
                <div style={{...s.tableWrap, marginTop:16}}>
                <table style={s.table}>
                  <thead><tr>
                    <th style={s.th}>Industrial User</th>
                    <th style={s.th}>IU Permit #</th>
                    <th style={s.th}>Control Mechanism</th>
                    <th style={s.th}>Categorical</th>
                    <th style={s.th}>Samples</th>
                    <th style={s.th}>Violations</th>
                    <th style={s.th}>Enforcement</th>
                    <th style={s.th}>SNC</th>
                  </tr></thead>
                  <tbody>
                    {annual.industrial_users?.map((iu: any) => (
                      <tr key={iu.company_id} style={{background: iu.in_snc ? "#fff5f5" : undefined}}>
                        <td style={s.td}><strong>{iu.name}</strong></td>
                        <td style={s.td}>{iu.iu_permit_number ?? <em style={{color:"#a0aec0"}}>—</em>}</td>
                        <td style={s.td}>
                          {iu.control_mechanism
                            ? <>{iu.control_mechanism.permit_number}
                                <span style={{color:"#718096", fontSize:11}}>
                                  {" "}(exp {iu.control_mechanism.expiration_date})
                                </span></>
                            : <em style={{color:"#c05621"}}>No permit</em>}
                        </td>
                        <td style={s.td}>
                          {iu.categorical?.is_categorical
                            ? <span style={s.catBadge}>{iu.categorical.cfr_part || "Categorical"}</span>
                            : <span style={{color:"#718096"}}>Non-cat.</span>}
                        </td>
                        <td style={s.td}>{iu.samples_submitted}</td>
                        <td style={{...s.td, ...(iu.violations > 0 ? {color:"#c53030", fontWeight:600} : {})}}>
                          {iu.violations}
                        </td>
                        <td style={s.td}>{iu.enforcement_actions}</td>
                        <td style={s.td}>
                          {iu.in_snc
                            ? <span style={s.sncBadge}>SNC</span>
                            : <span style={{color:"#a0aec0"}}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                {annual._note && (
                  <p style={s.disclaimer}>⚠ {annual._note}</p>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:         { minHeight:"100vh", background:"#f0f4f8" },
  stickyTop:    { position:"sticky", top:0, zIndex:100,
                  boxShadow:"0 2px 8px rgba(0,0,0,0.12)" },
  header:       { background:"#1a365d", color:"#fff", padding:"12px 24px",
                  display:"flex", alignItems:"center", gap:16, flexWrap:"wrap", rowGap:8 },
  brand:        { fontSize:20, fontWeight:700, flex:1, whiteSpace:"nowrap" },
  role:         { fontSize:13, background:"#2b6cb0", padding:"3px 10px", borderRadius:12 },
  logoutBtn:    { marginLeft:"auto", background:"transparent", color:"#fff",
                  border:"1px solid #fff", borderRadius:5, padding:"4px 12px", cursor:"pointer" },
  // On a narrow screen the tab row scrolls sideways instead of widening the page.
  tabs:         { display:"flex", gap:4, padding:"16px 24px 0", background:"#fff",
                  borderBottom:"2px solid #e2e8f0", overflowX:"auto" },
  tab:          { padding:"8px 20px", borderTop:"none", borderLeft:"none", borderRight:"none",
                  borderBottom:"2px solid transparent", background:"transparent",
                  cursor:"pointer", fontSize:14, color:"#4a5568", borderRadius:"5px 5px 0 0",
                  whiteSpace:"nowrap", flexShrink:0 },
  activeTab:    { background:"#ebf8ff", color:"#1a365d", fontWeight:700,
                  borderBottom:"2px solid #2b6cb0" },
  content:      { padding:24 },
  splitPane:    { display:"flex", gap:20, flexWrap:"wrap" },
  list:         { width:280, flexShrink:0 },
  card:         { background:"#fff", borderRadius:8, padding:14, marginBottom:10,
                  cursor:"pointer", border:"2px solid transparent",
                  boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  selectedCard: { border:"2px solid #2b6cb0" },
  viewToggle:   { display:"flex", marginBottom:10, border:"1px solid #cbd5e0", borderRadius:6, overflow:"hidden" },
  viewBtn:      { flex:1, padding:"7px 10px", fontSize:13, fontWeight:600, border:"none",
                  background:"#f7fafc", color:"#4a5568", cursor:"pointer" },
  viewBtnOn:    { background:"#2b6cb0", color:"#fff" },
  statusChip:   { display:"inline-block", marginTop:6, fontSize:11, fontWeight:700, borderRadius:4, padding:"2px 7px" },
  statusPending:{ background:"#fefcbf", color:"#975a16" },
  statusDone:   { background:"#c6f6d5", color:"#276749" },
  statusWithdrawn: { background:"#edf2f7", color:"#4a5568" },
  doneBox:      { background:"#f0fff4", border:"1px solid #9ae6b4", borderRadius:6, padding:"10px 14px",
                  fontSize:13, color:"#22543d", marginTop:12 },
  cardTop:      { display:"flex", justifyContent:"space-between", fontWeight:600, fontSize:14 },
  cardSub:      { fontSize:12, color:"#718096", marginTop:4 },
  fine:         { color:"#c53030", fontWeight:700 },
  detail:       { flex:1, minWidth:280, background:"#fff", borderRadius:8, padding:20,
                  boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  detailTitle:  { fontSize:16, fontWeight:700, color:"#1a365d", marginBottom:14 },
  evidenceBox:  { background:"#fffaf0", border:"1px solid #f6e05e", borderRadius:6,
                  padding:"12px 14px", marginBottom:16 },
  evidenceHead: { fontSize:12, fontWeight:700, color:"#975a16", textTransform:"uppercase" as const,
                  letterSpacing:0.3, marginBottom:8, display:"flex", justifyContent:"space-between" },
  evidenceParam:{ color:"#1a365d", fontWeight:600, textTransform:"none" as const, letterSpacing:0 },
  evidenceRow:  { display:"flex", justifyContent:"space-between", fontSize:13, padding:"3px 0",
                  borderBottom:"1px dotted #f0e6c8" },
  evidenceK:    { color:"#718096", fontWeight:600 },
  evidenceExceed:{ color:"#c53030", fontWeight:700 },
  evidenceNone: { fontSize:13, color:"#718096", fontStyle:"italic" as const },
  evidenceLink: { marginTop:10, background:"none", border:"none", padding:0, cursor:"pointer",
                  color:"#2b6cb0", fontWeight:600, fontSize:13 },
  letterBox:    { background:"#f7fafc", border:"1px solid #e2e8f0", borderRadius:6,
                  padding:14, fontFamily:"monospace", fontSize:12, whiteSpace:"pre-wrap",
                  marginBottom:16, maxHeight:260, overflowY:"auto" },
  label:        { display:"block", fontSize:12, fontWeight:600, color:"#4a5568", marginBottom:4 },
  textarea:     { display:"block", width:"100%", padding:"8px 10px", marginBottom:14,
                  border:"1px solid #cbd5e0", borderRadius:5, fontSize:13,
                  minHeight:80, resize:"vertical" },
  input:        { display:"block", width:"100%", padding:"8px 10px", marginBottom:14,
                  border:"1px solid #cbd5e0", borderRadius:5, fontSize:14 },
  actionRow:    { display:"flex", gap:10 },
  approveError: { marginTop:12, background:"#fff5f5", border:"1px solid #fc8181", borderRadius:6,
                  color:"#c53030", padding:"8px 12px", fontSize:13 },
  enfPending:   { color:"#975a16", fontWeight:600 },
  enfIssued:    { color:"#718096" },
  approveBtn:   { padding:"8px 20px", background:"#276749", color:"#fff",
                  border:"none", borderRadius:5, fontWeight:600, cursor:"pointer" },
  cancelBtn:    { padding:"8px 20px", background:"#718096", color:"#fff",
                  border:"none", borderRadius:5, cursor:"pointer" },
  empty:        { color:"#718096", padding:16 },
  allGood:      { color:"#276749", fontWeight:600, padding:16 },
  filterBar:    { display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap",
                  background:"#fff", padding:"12px 16px", borderRadius:8,
                  boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  filterLabel:  { fontSize:13, fontWeight:600, color:"#4a5568", whiteSpace:"nowrap" },
  filterSelect: { padding:"6px 10px", border:"1px solid #cbd5e0", borderRadius:5,
                  fontSize:13, minWidth:200 },
  clearFilter:  { padding:"5px 12px", background:"#718096", color:"#fff", border:"none",
                  borderRadius:5, cursor:"pointer", fontSize:12 },
  filterCount:  { fontSize:12, color:"#718096", marginLeft:"auto" },
  // Wide tables scroll inside this box on a narrow screen.
  tableWrap:    { overflowX:"auto", borderRadius:8, boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  table:        { width:"100%", borderCollapse:"collapse" as const, background:"#fff",
                  borderRadius:8, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.08)" },
  th:           { padding:"9px 12px", background:"#f7fafc", borderBottom:"2px solid #e2e8f0",
                  textAlign:"left" as const, fontSize:12, fontWeight:700, color:"#1a202c" },
  td:           { padding:"9px 12px", borderBottom:"1px solid #edf2f7",
                  fontSize:13, color:"#2d3748" },
  chipOverdue:  { padding:"4px 12px", borderRadius:12, fontSize:12, fontWeight:700,
                  background:"#fff5f5", color:"#c53030", border:"1px solid #feb2b2" },
  chipDueSoon:  { padding:"4px 12px", borderRadius:12, fontSize:12, fontWeight:700,
                  background:"#fffff0", color:"#744210", border:"1px solid #f6e05e" },
  chipNever:    { padding:"4px 12px", borderRadius:12, fontSize:12, fontWeight:700,
                  background:"#f7fafc", color:"#4a5568", border:"1px solid #cbd5e0" },
  chipCurrent:  { padding:"4px 12px", borderRadius:12, fontSize:12, fontWeight:700,
                  background:"#f0fff4", color:"#276749", border:"1px solid #9ae6b4" },
  schedOverdue: { fontSize:12, fontWeight:700, color:"#c53030" },
  schedDueSoon: { fontSize:12, fontWeight:700, color:"#744210" },
  schedNever:   { fontSize:12, color:"#718096" },
  schedCurrent: { fontSize:12, fontWeight:600, color:"#276749" },
  sevMajor:     { fontSize:12, fontWeight:700, color:"#c53030" },
  sevSignificant:{ fontSize:12, fontWeight:600, color:"#c05621" },
  sevMinor:     { fontSize:12, color:"#718096" },
  exportBtn:    { padding:"5px 12px", background:"#2b6cb0", color:"#fff", border:"none",
                  borderRadius:5, cursor:"pointer", fontSize:12, fontWeight:600 },
  reportHead:   { background:"#fff", borderRadius:8, padding:"16px 20px", marginBottom:16,
                  boxShadow:"0 1px 4px rgba(0,0,0,0.08)", borderLeft:"4px solid #2b6cb0" },
  reportTitle:  { fontSize:18, fontWeight:700, color:"#1a365d" },
  reportSub:    { fontSize:14, color:"#2d3748", marginTop:4 },
  reportMeta:   { fontSize:12, color:"#718096", marginTop:4 },
  cardGrid:     { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))",
                  gap:12, marginBottom:16 },
  statCard:     { background:"#fff", borderRadius:8, padding:"14px 16px",
                  boxShadow:"0 1px 4px rgba(0,0,0,0.08)", textAlign:"center" as const },
  statValue:    { fontSize:26, fontWeight:700, color:"#1a365d" },
  statLabel:    { fontSize:12, color:"#718096", marginTop:2 },
  sncBox:       { background:"#fff5f5", border:"1px solid #feb2b2", borderRadius:8,
                  padding:"12px 16px", marginBottom:8 },
  sncTitle:     { fontSize:14, fontWeight:700, color:"#c53030" },
  sncBadge:     { fontSize:11, fontWeight:700, color:"#fff", background:"#c53030",
                  padding:"2px 8px", borderRadius:10 },
  sncPubBox:    { background:"#f7fafc", border:"1px solid #e2e8f0", borderRadius:8,
                  padding:"12px 16px", marginTop:8 },
  sncPubOk:     { fontSize:12, fontWeight:600, color:"#2f855a" },
  sncPubWarn:   { fontSize:12, fontWeight:600, color:"#975a16" },
  sncPubInput:  { padding:"5px 8px", border:"1px solid #cbd5e0", borderRadius:5, fontSize:12 },
  catBadge:     { fontSize:11, fontWeight:600, color:"#2c5282", background:"#ebf8ff",
                  padding:"2px 8px", borderRadius:10 },
  disclaimer:   { fontSize:11, color:"#975a16", background:"#fffaf0",
                  border:"1px solid #f6e05e", borderRadius:6, padding:"8px 12px", marginTop:16 },
};
